import { createDocEntryStore } from './docStore';
import { IS_DEMO } from '../../apiBase';
import { readState, writeState } from './docState';
import { ease, eulerDegFromQuat, mirrorAssembly, qCompose, qInvert, qRotate, qslerp, quatFromEulerDeg, turnOfQuat } from './assemblyPose';
import type { Assembly, Instance, Turn, Vec3 } from './assemblyPose';
import type { SubStepFamily } from './subStepKinds';

// PHASE 5'S ANSWER: how one part goes onto another, ONE FITTING PER SUB-STEP.
//
// Per sub-step and not per document, which is the whole shape of this file. A fitting is
// what a particular sub-step DOES — the spring washer goes onto the screw in 1B, the seat
// goes onto the frame in 3A — so carrying one set of parts and one set of distances across
// a move from 1B to 3A would answer 3A's question with 1B's answer. Worse, it would look
// answered: the selectors come up filled, the numbers come up plausible, and nothing on
// screen says they were typed about a different fitting.
//
// The slot key is the whole of the addressing (see assemblySlots): `${category}#${letter}`
// for a sub-step. Not a position — a position moves the day a step is re-analysed with one
// sub-step more.
//
// Persisted through docState like every other phase's answers, under its own key, so it is
// on the server and shared by every admin. Rebuilt from there when the run changes, which
// is what makes the loader below synchronous.

// NOWHERE YET, which is what a document that has never been navigated is stored as — and
// what "0X", the hand-picked slot, used to be. That slot is gone (see assemblySlots); this
// key is kept because it is still the opening value of `at`, and because documents answered
// while it existed have a fitting filed under it that is nobody's business to delete.
//
// It is no longer a place the phase can stand: an `at` of this falls through to the first
// sub-step, which is the same thing the phase does with any key the manual no longer has.
export const MANUAL_SLOT = '';

// WHICH TWO PARTS, AND HOW ONE ARRIVES ON THE OTHER.
//
// Every distance is millimetres and every direction is in the TARGET's frame, which is also
// the frame `seat` is written in — so the moving part starts at `seat + axis × travel` with
// nothing converted in between.
// HOW A PART GOES IN, in the two words the analysis is offered. A closed pair rather than a
// boolean, because the day a bayonet or a press fit needs its own form this is where the
// third word goes, and `threaded: false` has nowhere to put one.
export type Drive = 'turned' | 'straight';

// One mark per part a movement draws — see modelMark. Null where that part had no model.
export type SignedOn = { target: string | null; moving: string | null; tool: string | null };

export type Fitting = {
  // WHETHER THIS ANIMATION IS SIGNED OFF. False until somebody has watched it and said so,
  // which is why it is false by default rather than derived from "both parts are chosen":
  // a fitting with two parts and a wrong axis is complete and wrong, and the phase's bar
  // must not count it. It is the only field here nothing suggests and nothing infers.
  //
  // A validated fitting is what Phase 6 publishes in place of an empty frame.
  validated: boolean;
  // WHICH MODEL OF EACH PART IT WAS SIGNED OFF ON — see fittingModels, which writes and reads
  // it. Absent on a sign-off given before it was kept.
  signedOn?: SignedOn;
  // WHY THE LAST SIGN-OFF WAS WITHDRAWN, where it was for a reason the marks above cannot tell:
  // Phase 4 changed the gesture (see gestureMoved). Said under the viewer in place of "another 3D
  // model", which the marks would claim once the part it drops is replaced. Gone with the next
  // sign-off.
  withdrawnFor?: 'gesture';
  // AND WHICH GESTURE IT WAS GIVEN ON — the slot's print (see gestureIds). A fitting is filed by
  // position and Phase 4 may put another gesture at that position; the parts can even be the
  // same two (1-2 was "the rail against the frame", and is "the barrel nuts into the rail"). Absent
  // on a sign-off given before it was kept.
  signedFor?: string;
  // WHERE THE MOVEMENT ON SCREEN CAME FROM: worked out by the sub-step's rule (its joint, its
  // hole, its set — see fromJoint, fromHole, fromSet), typed or dragged by HAND, or SAID by an
  // answer nobody has checked. It is what lets a rule place a sub-step without being asked:
  // a rule may write over nothing, over what was said, and over its own earlier answer — never
  // over a hand's. Absent on a fitting written before it was kept, which a rule leaves alone
  // unless it is still BLANK's (see awaitsPlacing).
  placedBy?: 'rule' | 'hand' | 'said';
  // AND WHICH MODELS THE RULE WORKED IT OUT ON, marked as a sign-off's are (see modelMark). A
  // rule's answer about a piece that has since been made another way is the answer to another
  // question, and is worked out again.
  placedOn?: SignedOn;
  // AND WHICH GESTURE IT WAS WORKED OUT FOR: the sub-step's parts, their roles and counts, and its
  // sentence, as Phase 4 gave them. A re-run that turns one screw into two is another question,
  // and a rule's answer to the first (one screw, no copies) is worked out again.
  placedFor?: string;
  // AND WHICH WORKING OF THE RULES' SHOT IT WAS (see MOVE_SHOT_V): a movement's shot is saved when
  // its rule places it, and one taken under older rules is placed again, unless signed off.
  shotV?: number;
  // THE MOVING PART IS THE OTHER HAND OF ITS PAIR, and is drawn as the kept file reflected (see
  // ScenePart.mirror). A pair is one model; which unit a sub-step brings is said by the joint
  // that places it (see fromJoint). Goes when the moving part does.
  mirrored?: boolean;
  // WHETHER THE PART HAS BEEN SLID HOME AGAINST THE OTHER'S SURFACE, or is still standing
  // where the contact's arithmetic put it.
  //
  // A contact names a point on each part's bounding BOX, and a box face is a plane through the
  // furthest point of a curved solid — so placing box on box leaves a rail resting against the
  // plane of a backrest's bulge rather than against the leg it meets. The seating pass fixes
  // that by measuring (see assemblySnap), and this is what stops it running twice: false means
  // "a contact has just landed and nobody has slid it in yet", true means it has been done.
  //
  // ABSENT ON EVERYTHING ELSE, and that is the third state doing real work: a fitting somebody
  // typed by hand, or one read from an answer that predates contacts, is not waiting to be
  // moved and must never be moved. Only a contact asks for this.
  seated?: boolean;
  // WHICH FAMILY OF GESTURE THIS SLOT IS: a combining, where two or three pieces leave as one, or a
  // placing, where a part is moved and fastened to nothing. The sub-step's own kind read down
  // to its family (see subStepKinds), copied onto the answer.
  //
  // A COPY, and it stays one. It used to decide whether the slot was answered — a combining
  // owing two parts and a placing one — and both owe two now, so that is not what it is for
  // any more. What still reads it is the form: a placing fastens nothing, so it has no thread
  // to describe and no pitch and depth to type. Kept fresh by the phase, which adopts the
  // slot's kind on arrival and whenever a re-analysis changes it.
  //
  // 'combining' on a whole-step slot, which has no analysis to be told by.
  kind: SubStepFamily;
  // The catalog items UNTICKED out of this sub-step's parts. What the dropdowns offer is
  // the slot's own parts minus these — the sub-step's list is the universe, and this is the
  // subtraction somebody made from it.
  //
  // STORED AS THE EXCLUSION AND NOT THE SELECTION, which is the difference between a list
  // that goes stale and one that cannot. Held the other way round, a fitting kept a snapshot
  // of the parts the sub-step had WHEN IT WAS ANSWERED: re-run the analysis, and 1A's stored
  // selection still named the barrel nut the model had since removed from it — the chip row
  // showing one part while the selector under it offered another. Every part the analysis
  // now names is ticked unless it was explicitly unticked, so a re-analysis lands ticked and
  // a part that leaves the sub-step leaves the dropdowns with it.
  //
  // CATALOG ITEMS, PARTS AND TOOLS TOGETHER, because the row they tick is one row. A tool's
  // exclusion reaches no dropdown of parts — those are drawn from the sub-step's parts, which
  // hold no tools — so the two kinds share this list without meeting in it.
  excluded: number[];
  // The catalog item that stands still. Null until somebody picks one.
  targetId: number | null;
  // The catalog item that goes onto it.
  movingId: number | null;
  // WHICH TOOL MAKES THE FITTING, as the tool's index in the Phase 2 parts list — not a
  // catalog id like the two above. It stays an index because a tool has to be NAMED whether
  // or not the Parts DB has ever seen it, and half the manuals' allen keys have no row at
  // all. Where there is one, the viewer draws the tool too: see useDocTools, which carries
  // the catalog id alongside the index rather than in place of it. Null for a fitting made
  // by hand.
  toolId: string | null;
  // AND A SECOND TOOL, FOR THE SECOND HARDWARE (see nextOn), where the sub-step lists two: the hex key
  // driving 2-2's JCBC screws after the PVC screwdriver turned the barrel nut. The same index as
  // `toolId`; riding each unit of the second hardware in turn where a set says where it bears on it
  // (`nextToolRide`, worked out on reading like `toolRide`).
  nextToolId?: string | null;
  nextToolRide?: Vec3 | null;
  // WHERE THE FIXED PART STANDS, in the assembly's own frame — the frame the gizmo draws at
  // width 0, depth 0, height 0. [0,0,0] until somebody drags it, which is what it was
  // silently before this field existed.
  //
  // It is not the same question as `seat` and does not replace it: `seat` is the moving part
  // measured FROM this one, so the two parts keep their relationship when either is moved.
  // Dragging the fixed part carries whatever is seated on it along, exactly as a parent does
  // in Blender — see assemblyOf, where that parenting already lived.
  targetSeat: Vec3;
  // HOW EACH PART IS TURNED WHERE IT SEATS, as XYZ Euler degrees in the ASSEMBLY's frame —
  // the frame the gizmo draws at the origin and the frame the panel's boxes are read in.
  //
  // The three of them mirror the three seats, and they are the answer to a question that had
  // none: a mesh comes out of the Parts DB in whatever orientation it was reconstructed in,
  // and until now the only reply to "this leg is lying on its side" was to go and fix the
  // mesh. `turn` cannot do it — that is the GESTURE, the rotation a placing arrives through
  // and unwinds to nothing by the time it seats — and the thread's roll cannot either, being
  // a function of the travel. Neither is where a part RESTS.
  //
  // Stored in the assembly's frame for all three, including the moving part whose seat is
  // written in the fixed part's: one frame is what the panel shows and what a person reads
  // off the gizmo, and the conversion into the fixed part's frame happens once, in
  // assemblyOf, rather than in every place that reads or writes one.
  spin: Vec3;
  targetSpin: Vec3;
  toolSpin: Vec3;
  // WHERE THE TOOL STANDS, in the assembly's frame. The origin until somebody puts it
  // somewhere, which is where a thing with no place of its own has to start.
  //
  // It has no seat in the sense the two parts do, and that is the whole of why it gets a
  // field of its own rather than sharing theirs: a fitting is one part going ONTO another
  // and the tool is joined to neither. It stands beside them, held wherever a hand holds it,
  // which is a position and nothing more.
  toolSeat: Vec3;
  // WHERE THE TOOL BEARS ON THE MOVING PART, as a hardware set built the two together — the PVC
  // screwdriver's tip in the barrel nut's slot, the hex key in the screw's head — centre to centre
  // in the moving part's frame. Worked out on reading (see toolRideOf), never by a hand: where it
  // is given and nobody has put the tool anywhere, the tool rides on the moving part, travelling
  // and turning with it, instead of standing at the origin while the part moves without it.
  toolRide?: Vec3 | null;
  // AND IT BEARS ON THE HARDWARE PREPARED ON THE MOVING PART, not on the part: the PVC screwdriver
  // turning each barrel nut into the rail, then leaving before the rail goes against the frame
  // (see Fitting.prepOn). Worked out on reading, with `toolRide`.
  toolOnPrep?: boolean;
  // OR ON THE SECOND HARDWARE (see Fitting.nextOn): the PVC screwdriver turning the barrel nut put
  // in after the dowels. It comes in for that hardware alone, and is not drawn before.
  toolOnNext?: boolean;
  // THE HARDWARE PREPARED IN THE HAND THAT THIS GESTURE PLAYS FIRST (see foldsOf): the washer
  // sliding onto the screw this gesture then puts in, drawn inside the prepared screw's piece.
  // Worked out on reading (see usePreps), never stored, like `toolRide`.
  preps?: Prep[] | null;
  // AND THE HARDWARE PREPARED ON THE MOVING PART IN THE HAND, where Phase 4 lists it with this
  // gesture ("prepared", see the prompt): the washer slid onto the screw before the screw goes in.
  // Where it rests on the moving part, the way it comes and how far, in the moving part's frame —
  // worked out by the hardware set that built the two together (see setPlacement), on arrival,
  // for this moving part (`onto`). Played first, on every copy (see assemblyOf).
  // OR PUT INTO THE MOVING PART, where it is a component: the barrel nuts into the side rail before
  // the rail goes against the frame — placed by the hole they go into (see useHolePlacement).
  prepOn?: PrepOn | null;
  // AND THE SECOND HARDWARE THE GESTURE PUTS INTO THE SAME PART, after the first: the wood dowels
  // pushed into the end of the rail its barrel nut has just gone into — one sub-step, as Phase 4
  // lists them (two "added", in order). Where each unit rests, in the fitted-onto part's frame
  // (`onto`, the target), placed by its hole (see useHolePlacement). Played last, all at once.
  nextOn?: PrepOn | null;
  // AND THE HARDWARE PREPARED ON THAT SECOND HARDWARE IN THE HAND: the spring washer slid onto each
  // JCBC screw before it goes into the barrel nut put in first. Where it rests on the second
  // hardware, in that hardware's frame (`onto`), by the set that built the two (see setPlacement);
  // on every unit, in a slice of its own before they go in.
  nextPrep?: PrepOn | null;
  // AND A THIRD HARDWARE, INTO THE SAME PART, after the second: the wood button over the JCBC screw
  // just driven into its barrel nut. Placed by its holes (see useHolePlacement's `last`), in the
  // fitted-onto part's frame (`onto`, the target); played last, all at once, under the second
  // hardware's shot, once every tool is put down.
  lastOn?: PrepOn | null;
  // AND THE WHOLE GESTURE REFLECTED LEFT TO RIGHT: a repeat on the other side from the sub-step it
  // repeats (see Slot.repeat.mirror) — the right rail's barrel nuts, drawn as the left's in a
  // mirror. Worked out on reading, never stored.
  mirrorAll?: boolean;
  // AND IT OPENS ON THE WHOLE: the very first sub-step of the manual, which starts from its parts
  // seen whole, from its shot's angle, and comes in to its shot before anything moves (INTRO).
  // Worked out on reading, never stored.
  opensWide?: boolean;
  // Where the moving part ends up, measured centre to centre. Both meshes are re-origined to
  // the middle of their own bounding box when they load (see assemblyScene), so [0,0,0] means
  // "concentric", which for a washer on a screw is nearly right before any nudging.
  seat: Vec3;
  // Which way it comes in from.
  axis: Vec3;
  // How far out along that axis it starts.
  travel: number;
  // THE OTHER COPIES OF THE MOVING PART, where one gesture puts several on — four felt pads under
  // four feet, a button over each screw (Phase 4 writes such a run of one cover as one sub-step,
  // see stepAnalyzePrompt). Each is where its copy rests and how it comes in, in the frames `seat`,
  // `spin` and `axis` are in; the thread and the turn are the first copy's, the part being the
  // same. Absent on every gesture that moves one thing, which is nearly all of them.
  copies?: Copy[];
  // HOW IT IS TURNED AS IT ARRIVES — a placing's answer, and a piece's turned into line in its hole
  // (a barrel nut, see holeMovement); null for a part that is only brought in or set down. Its axis
  // is in the fixed part's frame. Written by the gesture selector, which offers a closed
  // list rather than an axis and an angle to type, for the reason the six directions above
  // are a closed list: a part is laid flat, stood upright or turned over, and a free-hand
  // angle is three fields to mistype for a case that does not arise.
  turn: Turn | null;
  // HOW THE MOVING PART GOES IN: wound home, or brought straight in.
  //
  // A QUESTION ABOUT THE JOINT, which is why it is stored beside the two numbers rather than
  // inferred from them. A nut and a washer travelling the same 40 mm have the same seat, the
  // same axis and the same travel; what separates them is that one is turned the whole way.
  // Answered by the analysis (see FittingAnswer.drive) and overridable here.
  //
  // It governs the FORM and not the geometry: 'straight' is what makes the pitch and the
  // thread disappear from the phase, and clearing them is what makes the movement straight.
  // Nothing reads this to decide how to animate — see below, where the two numbers still
  // decide that alone — so a fitting cannot say one thing and play another.
  drive: Drive;
  // THREADED, or not. A washer slides on and this stays 0; a nut turns on and the last
  // `depth` millimetres are a helix at `pitch` mm per turn — the one motion that cannot be
  // keyframed honestly, since the turn is not decoration on the travel but a function of it.
  pitch: number;
  depth: number;
  // WHERE THE SHOT IS TAKEN FROM, at each end of the sub-step.
  //
  // A published clip is a movement AND a camera, and only one of those was being answered.
  // Two sub-steps of one step can want opposite views — a barrel nut going into the inner
  // face of a rail is invisible from the side the rail is drawn from — and until now the
  // camera was whatever the last person had dragged it to, which is to say not recorded at
  // all. It is part of what gets signed off and part of what gets published, so it is stored
  // per sub-step like everything else here.
  //
  // IN THE SAME TERMS THE VIEWER'S OWN CORNER READS OUT, which is what makes them checkable
  // against the picture: `camPos` is the point the shot is centred on, in the assembly's
  // frame and in millimetres; `camRot` is how far the view is turned off the square-on home,
  // as XYZ Euler degrees; `camZoom` is a multiple of the home distance. Three facts, and all
  // three are needed — an orientation without a distance is not a shot.
  camStartPos: Vec3;
  camStartRot: Vec3;
  camStartZoom: number;
  camEndPos: Vec3;
  camEndRot: Vec3;
  camEndZoom: number;
};

// Every slot's answer, and which one is open. `at` is a view position and is persisted with
// the rest on purpose: coming back to this phase on the sub-step you left it on is the
// difference between a phase you resume and one you re-navigate.
export type Book = { at: string; bySlot: Record<string, Fitting> };

const KEY = 'notice.assembly';

// THE VIEWPOINT, AND THERE IS ONLY THE ONE: square on the origin, at the framing distance,
// the same at both ends of every sub-step. Straight at the parts, and nothing moves.
//
// It is a constant here rather than an answer because it used to be an answer — Phase 5's call
// was asked for a "camStart" and a "camEnd" and gave back a shot per sub-step. That was a
// second question to get wrong on top of the movement, and a shot that tips, swings and zooms
// for reasons the model liked is harder to follow than one that holds. See
// subStepFittingPrompt, where the ask used to be.
//
// The camera fields below are still per sub-step and still settable by hand — the viewer's
// "grab this shot" button writes them. This is what they open on, and what the ask button
// puts them back to.
export const FIXED_CAM = {
  pos: [0, 0, 0] as Vec3,
  rot: [0, 0, 0] as Vec3,
  zoom: 1,
};

// The same shot as the six fields a fitting stores it in, both ends. Its own function because
// two places write it — the blank fitting below and the suggestion the ask button applies —
// and because it hands out FRESH arrays: a Vec3 shared between two fittings is one fitting's
// drag showing up in another's numbers.
export type CamFields = Pick<
  Fitting,
  'camStartPos' | 'camStartRot' | 'camStartZoom' | 'camEndPos' | 'camEndRot' | 'camEndZoom'
>;

export const fixedCam = (): CamFields => ({
  camStartPos: [...FIXED_CAM.pos] as Vec3,
  camStartRot: [...FIXED_CAM.rot] as Vec3,
  camStartZoom: FIXED_CAM.zoom,
  camEndPos: [...FIXED_CAM.pos] as Vec3,
  camEndRot: [...FIXED_CAM.rot] as Vec3,
  camEndZoom: FIXED_CAM.zoom,
});

// A SHOT FROM `round` DEGREES ROUND THE UPRIGHT AND `tip` ABOVE THE LEVEL, as the three angles a
// shot is written in (a tip about X, then a turn about the tipped Y — see the scene's `look`, which
// keeps their direction and stands the camera level). Written out because the obvious [−tip,
// round, 0] is not that direction: it is about 11° up and 41° round for (15, 40).
export const levelTurn = (tip: number, round: number): Vec3 => {
  const rad = (d: number) => (d * Math.PI) / 180;
  const deg = (r: number) => (r * 180) / Math.PI;
  const y = Math.asin(Math.cos(rad(tip)) * Math.sin(rad(round)));
  return [-deg(Math.asin(Math.sin(rad(tip)) / Math.cos(y))), deg(y), 0];
};

// Concentric, arriving along +Z, from 40 mm out and not threaded. Chosen so that picking two
// parts alone puts something moving on screen: a fitting nobody has touched is one you can
// already play, and the numbers are then adjusted against what you see rather than typed
// into an empty form.
export const BLANK: Fitting = {
  validated: false,
  // The two-part question, which is the one every fitting written before placings existed
  // was answering — so a stored fitting read back through `bookFrom` lands on the kind it
  // was actually given.
  kind: 'combining',
  excluded: [],
  targetId: null,
  movingId: null,
  toolId: null,
  targetSeat: [0, 0, 0],
  spin: [0, 0, 0],
  targetSpin: [0, 0, 0],
  toolSpin: [0, 0, 0],
  toolSeat: [0, 0, 0],
  seat: [0, 0, 0],
  axis: [0, 0, 1],
  travel: 40,
  turn: null,
  // STRAIGHT UNTIL SOMETHING SAYS OTHERWISE. Most sub-steps of most manuals are: a part is
  // offered up, slid on, pushed home. A default of 'turned' would put two fields on every
  // fitting in the app to be left at zero, which is the form asking a question nobody has.
  drive: 'straight',
  pitch: 0,
  depth: 0,
  // THE VIEW THE PHASE OPENS ON, at both ends: the fixed viewpoint above. Which is a real
  // answer rather than a placeholder — it is the reference every other camera number is
  // measured against, and a sub-step nobody has re-framed is honestly framed dead-on.
  ...fixedCam(),
};

// THE NUMBERS THAT SAY WHERE THE PARTS STAND AND HOW ONE ARRIVES — what a rule writes and what
// a hand corrects. Not the camera, the tool or the sign-off: none of those is the placement.
const POSE = ['targetSeat', 'targetSpin', 'seat', 'spin', 'axis', 'travel', 'copies', 'turn', 'drive', 'pitch', 'depth'] as const;

// WHETHER A RULE MAY PLACE THIS WITHOUT BEING ASKED: nothing has placed it — it stands as BLANK
// does — or what did was an answer, which the rule is there to replace.
export const awaitsPlacing = (f: Fitting): boolean => f.placedBy === 'said' || (f.placedBy == null
  && POSE.every((k) => JSON.stringify(f[k]) === JSON.stringify(BLANK[k])));

// What is on disk, which is one of two shapes.
//
// The first version of this phase stored a SINGLE fitting per document, with the slot it
// belonged to written inside it. Read as a book that would be an empty book — the phase
// would come up blank on a document somebody had already answered, which reads as work
// lost. So a stored fitting is recognised by its own fields and filed under the slot it
// names, which is exactly where it was being shown.
// A stored fitting minus the field that flipped. `allowed` was the ticked list, and there is
// nothing to convert it into: "what was ticked" cannot become "what was unticked" without the
// list it was ticked out of, which is the sub-step's and is not in here. Dropped, so a fitting
// written before the flip opens with everything its sub-step names ticked — which is the new
// default, and the honest reading of a list that was never a subtraction.
function clean(raw: object): Partial<Fitting> {
  const rest: Record<string, unknown> = { ...raw };
  delete rest.allowed;
  // 'fitting' WAS THE OTHER WORD, and every answer on disk carries it. The two families are
  // the sub-step kinds' now — placing and combining — and 'placing' is spelled the same in
  // both vocabularies, so only this one needs saying.
  //
  // Nothing DRAWS differently for it: the one thing the word still decides is whether there
  // is a thread to describe. What it breaks is the phase's "has the slot's kind changed" effect,
  // which compares the stored word against the slot's — never equal again, so every stored
  // fitting would be rewritten on arrival, for nothing. This is the one funnel they are all
  // read through, so it is the one line that fixes it.
  if (rest.kind === 'fitting') rest.kind = 'combining';
  else if (rest.kind !== 'placing' && rest.kind !== 'combining') delete rest.kind;

  // AND A FITTING FROM BEFORE THE WORD EXISTED IS READ OFF ITS OWN NUMBERS. Every answer on
  // disk predates `drive`, and BLANK's default would call all of them straight — which on the
  // ones that thread would take two filled-in fields off the form while the movement went on
  // playing the helix they describe. A fitting with a pitch AND a depth was turned in; that is
  // the whole of the test, because it is the same test assemblyOf has always applied.
  if (rest.drive !== 'turned' && rest.drive !== 'straight') {
    rest.drive = Number(rest.pitch) > 0 && Number(rest.depth) > 0 ? 'turned' : 'straight';
  }
  return rest as unknown as Partial<Fitting>;
}

export function bookFrom(raw: Record<string, unknown> | null | undefined): Book {
  if (raw && typeof raw === 'object' && 'bySlot' in raw) {
    const book = raw as unknown as Partial<Book>;
    const bySlot: Record<string, Fitting> = {};
    for (const [key, value] of Object.entries(book.bySlot ?? {})) {
      bySlot[key] = { ...BLANK, ...clean(value) };
    }
    return { at: typeof book.at === 'string' ? book.at : MANUAL_SLOT, bySlot };
  }
  // The old single-fitting shape, or nothing at all.
  if (raw && ('targetId' in raw || 'movingId' in raw)) {
    const old = raw as Partial<Fitting> & { stepKey?: string | null; subStep?: string | null };
    const at = old.stepKey ? `${old.stepKey}#${old.subStep ?? ''}` : MANUAL_SLOT;
    return { at, bySlot: { [at]: { ...BLANK, ...clean(old) } } };
  }
  return { at: MANUAL_SLOT, bySlot: {} };
}

const store = createDocEntryStore<{ book: Book }>((docId) => ({
  snapshot: { book: bookFrom(readState<Record<string, unknown>>(docId, KEY, {})) },
}));

const save = (docId: string, book: Book) => {
  store.patch(docId, { book });
  // Debounced and merged server-side by docState, so dragging a number field is one write
  // once it settles rather than one per keystroke.
  writeState(docId, KEY, book);
};

export const useBook = (docId: string): Book => store.useSnapshot(docId).book;

export const fittingAt = (book: Book, key: string): Fitting => book.bySlot[key] ?? BLANK;

export const useSlotAt = (docId: string): string => useBook(docId).at;

// Move to another slot. Nothing is carried over — the new slot's own answer is whatever it
// was left as, and BLANK if it has none.
export function goToSlot(docId: string, key: string) {
  save(docId, { ...store.get(docId).book, at: key });
}

// Patch the OPEN slot's fitting. Every control on the phase goes through here, so no control
// can write into a slot other than the one on screen.
//
// AND ANY CHANGE TO THE MOVEMENT UN-VALIDATES IT. A sign-off is about an animation somebody
// watched; nudge the seat by 2 mm afterwards and it is a different animation with the old
// tick still on it — which is the one failure this flag exists to prevent, since a tick
// nobody would notice going stale is worse than no tick at all. Only a patch that is ITSELF
// about validation leaves it alone, so ticking is not immediately undone by its own write.
//
// EXCEPT IN THE DEMO, where the tick is not this app's to retract. It records what somebody
// judged on prod, it cannot be given back (the toggle is not there) and it cannot be saved,
// so a visitor nudging a part to see how the phase works would be silently withdrawing
// somebody else's sign-off — and taking the sub-step off screen with it, since the demo
// only stands where a movement was validated. Nudge all you like: the judgement is prod's,
// and a refresh puts the movement back where prod left it.
export function setFitting(docId: string, patch: Partial<Fitting>) {
  setFittingAt(docId, store.get(docId).book.at, patch);
}

// THE SAME, INTO A NAMED SLOT — a validated joint fills its sub-steps' slots, none of which need be
// the one on screen (see jointMoves).
export function setFittingAt(docId: string, key: string, patch: Partial<Fitting>) {
  const book = store.get(docId).book;
  const clears = !IS_DEMO && !('validated' in patch);
  // A PLACEMENT WRITTEN BY NOBODY IN PARTICULAR IS A HAND'S. The rules and the answers say who
  // they are (`placedBy` in the patch); every other write of these numbers is a field typed or
  // a part dragged, from a dozen controls that should not each have to remember to say so.
  const byHand = !('placedBy' in patch) && POSE.some((k) => k in patch);
  const was = fittingAt(book, key);
  const otherPart = 'movingId' in patch && patch.movingId !== was.movingId && !('mirrored' in patch);
  // ANOTHER PART, FIXED OR MOVING, IS ANOTHER GESTURE: what the last sign-off was given on goes
  // with it. Kept, its marks are of parts no longer drawn, and the line under the viewer read them
  // as "signed off on another 3D model" of parts whose model had not changed (see changedSince).
  const otherGesture = !('signedOn' in patch) && (
    ('movingId' in patch && patch.movingId !== was.movingId)
    || ('targetId' in patch && patch.targetId !== was.targetId));
  const next = {
    ...was, ...patch,
    ...(otherGesture ? { signedOn: undefined, signedFor: undefined } : {}),
    // A copy of another part is no copy of this one.
    ...(otherPart ? { mirrored: undefined, ...('copies' in patch ? {} : { copies: undefined }) } : {}),
    ...(byHand ? { placedBy: 'hand' as const, placedOn: undefined } : {}),
    ...(clears ? { validated: false } : {}),
  };
  save(docId, { ...book, bySlot: { ...book.bySlot, [key]: next } });
}

// Every slot as it stands, read outside a render (see jointMoves).
export const bookOf = (docId: string): Book => store.get(docId).book;

// THE WHOLE BOOK AT ONCE, as it is re-filed when Phase 4 cuts a step again (see refile). Nothing
// here un-validates: the answers are the same answers, in the places their gestures went to.
export const replaceBook = (docId: string, book: Book) => save(docId, book);

// Whether a slot has an answer at all is asked below, at fittingChosen: the two parts, which
// are the only fields a fitting does not come with a value for (see BLANK).
// A MOVEMENT SAID AS ITS TWO ENDS — where the moving part stands at t=0 and where it comes to
// rest at t=1, each a position and an orientation, in the units the phase reads and writes in:
// CENTIMETRES and DEGREES, in the assembly's own frame.
//
// The fitting does not store either end. It stores the rest (`seat`, `spin`) and the movement
// that arrives there (`axis`, `travel`, `turn`), and the start is those composed — which is
// why nothing can go stale and why a part dragged somewhere new keeps its approach. The two
// functions below are the conversion, and they are HERE rather than in the phase because
// three callers need exactly the same one: the two tables somebody types into, and Phase 5's
// answer, which now names both ends and nothing else.
export type PoseSaid = { pos: Vec3; rot: Vec3 };

// CENTIMETRES IN, MILLIMETRES OUT. The call answers in cm — the unit a person reads a piece
// of furniture in — and every number this store holds is mm. Exported because the fixed part's
// own pose now arrives the same way and has to cross the same line.
export const mm = (v: Vec3): Vec3 => [v[0] * 10, v[1] * 10, v[2] * 10];

// A PLACE IN THE ASSEMBLY'S FRAME, WRITTEN AS A SEAT ON THE FIXED PART — which is where the
// moving part's seat is measured, and the fixed part may itself be turned.
//
// The subtraction alone was the whole of this, and it is right only while the fixed part
// stands square: `compose` turns a child's seat by its parent's rotation before adding it, so
// undoing that means turning the difference back the other way first. With a target spun 90°
// the two disagree by the whole of the offset — one place said in the panel and another drawn
// in the viewer.
export function seatOn(base: Vec3, baseSpin: Vec3, to: Vec3): Vec3 {
  const back = qRotate(
    qInvert(quatFromEulerDeg(baseSpin)),
    [to[0] - base[0], to[1] - base[1], to[2] - base[2]],
  );
  return [back[0], back[1], back[2]];
}

// WHERE IT ENDS UP: the seat and the spin, and nothing about the movement. Moving the rest
// slides the whole movement with it — "50 mm in along +Z" is still true of a part put
// somewhere else — which is what makes this half answerable on its own.
// IN THE FIXED PART'S FRAME, whatever the family: every gesture has a part it arrives on, so
// there is always something to measure the seat against. A placing used to be written in the
// assembly's frame instead, having no fixed part to measure from — see fittingChosen for why
// that is no longer a state a fitting can be in.
export function restOf(
  final: PoseSaid, targetSeat: Vec3, targetSpin: Vec3,
): Pick<Fitting, 'seat' | 'spin'> {
  const to = mm(final.pos);
  const spin: Vec3 = [final.rot[0], final.rot[1], final.rot[2]];
  return { seat: seatOn(targetSeat, targetSpin, to), spin };
}

// AND HOW IT GETS THERE, from the two ends: the direction is where the start lies from the
// rest, the travel is how far, and the turn is the rotation that unwinds into the seated one.
// The rest is untouched — it is the other function's answer — so what a start can change is
// only the approach, which is the only thing left that it can be about.
//
// THE DIRECTION IS A FREE VECTOR, not one of the six words: it is stored as given rather than
// snapped, because rounding a start to the nearest of six would move the part somewhere
// nobody asked for, silently. A start EQUAL to the rest leaves the direction alone — there is
// no direction in a distance of nothing, and a guess would be lost the moment a travel came
// back.
export function approachOf(
  start: PoseSaid, final: PoseSaid, targetSpin: Vec3,
): Partial<Pick<Fitting, 'axis' | 'travel' | 'turn'>> {
  const away = mm([
    start.pos[0] - final.pos[0], start.pos[1] - final.pos[1], start.pos[2] - final.pos[2],
  ]);
  // Out of the assembly's frame and into the one the approach is written in, which is the
  // fixed part's. Identical whenever that part stands square.
  const local = qRotate(qInvert(quatFromEulerDeg(targetSpin)), away);
  const travel = Math.hypot(local[0], local[1], local[2]);
  const reach = qCompose(quatFromEulerDeg(start.rot), qInvert(quatFromEulerDeg(final.rot)));
  const fixed = quatFromEulerDeg(targetSpin);
  const inFrame = qCompose(qInvert(fixed), qCompose(reach, fixed));
  return {
    travel: Math.round(travel * 10) / 10,
    ...(travel > 1e-6
      ? { axis: [local[0] / travel, local[1] / travel, local[2] / travel] as Vec3 }
      : {}),
    turn: turnOfQuat(inFrame),
  };
}

// A FITTING IS ANSWERED WHEN IT HAS BOTH ITS ENDS — the part that moves and the part it
// arrives on — whatever family it is.
//
// A PLACING USED TO OWE ONLY ONE, and that was right for exactly as long as there was a kind
// of sub-step that moved a single part and joined it to nothing. There is not: "position a
// single part" is gone (see subStepKinds), so every gesture in the vocabulary names two
// things, and a placing differs from a combining in one respect only — nothing is fastened.
//
// What the old rule bought was a slot that read as complete while half of it was thrown away:
// "position the side rail (C) against the frame (A)" counted as answered, drew the rail alone
// in an empty room, and published that.
export const fittingChosen = (f: Movement) => f.movingId != null && f.targetId != null;

// A FITTING WITH ITS BOOKKEEPING TAKEN OFF: everything needed to DRAW the sub-step, and
// nothing about the answering of it. `validated` is a click somebody made and `excluded` is
// which chips are ticked — both are facts about the phase, not about the movement, and
// neither survives a trip out of this browser.
//
// Which is what this type is for: the clip published with a manual is a Movement (see
// assemblyClip), so the public page draws from exactly the fields the phase draws from. A
// Fitting is assignable to one, so everything here goes on taking whichever it is handed.
export type Movement = Omit<Fitting, 'validated' | 'excluded' | 'seated'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const movementOf = ({ validated, excluded, seated, ...rest }: Fitting): Movement => rest;

// WHERE THE CAMERA IS AT `t`, flying between the two ends the fitting records.
//
// Shared, and not because it is long: the phase's viewer and the clip player draw the same
// movement, and a shot that interpolated by two rules would be two different clips of one
// fitting — the one signed off here, and the one a visitor is shown.
export function camAt(f: Movement, t: number): CamAt {
  // THE WHOLE FIRST, held, then flown into the shot, while nothing moves (see Fitting.opensWide).
  if (f.opensWide) {
    const rest = { ...f, opensWide: false };
    const i = INTRO / gestureTurns(f);
    if (t >= i) return camAt(rest, (t - i) / (1 - i));
    // FROM THE ANGLE OF THE SHOT IT FLIES INTO, whichever plays first — the barrel nuts' own where
    // the gesture prepares them, not the movement's (Louis 2026-10-28): the way in is then a push,
    // with no turn in it. At zoom 1, on the middle of the parts (see CamAt.auto).
    const first = camAt(rest, 0);
    const wide = shotAt({ ...fixedCam(), camStartRot: first.deg, camEndRot: first.deg }, 0);
    const k = t / i;
    return k < INTRO_HOLD ? wide : shotBetween(wide, first, ease((k - INTRO_HOLD) / (1 - INTRO_HOLD)));
  }
  // ON THE HARDWARE PREPARED IN THE PART FIRST, where it has a shot of its own (see PrepOn.shot):
  // the barrel nuts going into the rail are a centimetre each, on its inner face, and the shot of
  // the rail against the frame showed neither. Held through the preparation, then flown to the
  // movement's own while the tool is put down and the part waits (see Instance.leaves).
  // AND ON THE SECOND HARDWARE, LAST, where it has one (see Fitting.nextOn): flown to as its
  // slice opens, while it waits for the tool to be put down.
  const turns = gestureTurns(f);
  const prep = prepOf(f)?.shot && prepTurns(f) ? prepOf(f)!.shot! : null;
  const next = nextOf(f)?.shot ?? null;
  const b = prep ? prepTurns(f) / turns : 0;
  // Where the movement's slices end: before the second hardware's, and before the tool's closing
  // beat, through which the shot holds still.
  const e = 1 - (nextTurns(f) + lastTurns(f) + toolOutro(f)) / turns;
  const w = HOP_SHARE / turns;
  const mirror = f.mirrorAll;
  if (prep && t <= b) return shotAt(prep, t / b, mirror);
  const end = 1 - toolOutro(f) / turns;
  // HELD WHILE THE TOOL IS PUT DOWN, then flown (see toolLead).
  const lead = toolLead(f) / turns;
  if (next && t >= e + lead + w) return shotAt(next, Math.min(1, (t - e - lead - w) / (end - e - lead - w)), mirror);
  const from = b + (prep ? w : 0);
  const main = shotAt(f, (Math.min(t, e) - from) / (e - from), mirror);
  if (prep && t < from) return shotBetween(shotAt(prep, 1, mirror), main, ease((t - b) / w));
  if (next && t > e + lead) return shotBetween(main, shotAt(next, 0, mirror), ease((t - e - lead) / w));
  return main;
}

type Trio = [number, number, number];
// `auto`: how much of the parts' middle is added to `centre` (see the scene's outlineMiddle). 1 for
// a shot aimed at [0,0,0], which is the default and the opening wide shot, and which is centred on
// what is in the picture rather than on the origin (Louis 2026-10-28). Between 0 and 1 on its way
// to or from one. An aim anywhere else is a point somebody chose, and is kept.
export type CamAt = { centre: Trio; deg: Trio; zoom: number; auto?: number };
// ONE TURN, THE SHORT WAY, and not three angles each moved on its own. (−180°, 25°, 180°) is a
// turn of 155° about the upright, written another way: angle by angle, the camera tumbled over
// all three axes on its way to (0°, −65°, 0°), where it only had to go round the chair.
const turnTo = (a: Trio, b: Trio, k: number): Trio => {
  if (k <= 0) return a;
  if (k >= 1) return b;
  const d = eulerDegFromQuat(qslerp(quatFromEulerDeg(a), quatFromEulerDeg(b), k));
  return [d[0], d[1], d[2]];
};
export const shotBetween = (a: CamAt, b: CamAt, k: number): CamAt => ({
  centre: [0, 1, 2].map((i) => a.centre[i] + (b.centre[i] - a.centre[i]) * k) as Trio,
  deg: turnTo(a.deg, b.deg, k),
  // GEOMETRICALLY, because a zoom is a ratio. Ramped linearly, ×1 to ×4 spends its first half
  // getting to ×2.5 and its second crawling — the same push-in reads as decelerating when it is
  // not. Halfway between ×1 and ×4 is ×2.
  zoom: Math.exp(Math.log(a.zoom) + (Math.log(b.zoom) - Math.log(a.zoom)) * k),
  ...((a.auto || b.auto) ? { auto: (a.auto ?? 0) + ((b.auto ?? 0) - (a.auto ?? 0)) * k } : {}),
});
// ONE SHOT'S TWO ENDS, at `t`.
export function shotAt(c: CamFields, t: number, mirror?: boolean): CamAt {
  // A REFLECTED GESTURE IS WATCHED IN THE MIRROR TOO (see Fitting.mirrorAll): the centre across x,
  // and the turns about y and z the other way.
  const m = mirror ? [-1, 1, 1] : [1, 1, 1];
  const r = mirror ? [1, -1, -1] : [1, 1, 1];
  const at = (pos: Vec3, rot: Vec3, zoom: number): CamAt => ({
    centre: [0, 1, 2].map((i) => m[i] * pos[i]) as Trio,
    deg: [0, 1, 2].map((i) => r[i] * rot[i]) as Trio,
    zoom: zoom > 0 ? zoom : 1,
    ...(pos.every((v) => v === 0) ? { auto: 1 } : {}),
  });
  return shotBetween(
    at(c.camStartPos, c.camStartRot, c.camStartZoom),
    at(c.camEndPos, c.camEndRot, c.camEndZoom),
    ease(Math.min(1, Math.max(0, t))),
  );
}

// "EVERY SLOT WITH A SIGNED-OFF ANIMATION" WAS ASKED HERE, of the book alone, and it is not a
// question the book can answer any more: a sub-step that repeats another has no key in it and
// is signed off all the same (see Slot.repeat). Both readers — the phase's bar and the clips
// Phase 6 publishes — walk the SLOTS now and resolve each one through fittingFor.

// THE FITTING, AS SOMETHING poseAt CAN BE ASKED ABOUT.
//
// The target sits at step 0 — already standing when the step opens, which is what a part
// being fitted ONTO is — so the whole of step 1 belongs to the part that moves. Split across
// two orders instead, half the scrubber would drive a part that has nowhere to go.
export function assemblyOf(f: Movement): Assembly {
  if (!fittingChosen(f)) return { instances: [] };
  const prep = prepOf(f);
  const base = 1 + prepTurns(f);
  // TWO PARTS, WHATEVER THE FAMILY. A placing drew one — itself, at the origin, arriving from
  // nowhere onto nothing — which was the honest picture of a gesture that joined nothing to
  // anything. No such gesture exists now (see fittingChosen), and the picture it left behind
  // was a side rail floating in an empty room while the frame it is being held against sat in
  // the answer, named and unposed and undrawn.
  const target: Instance = {
    id: 'target',
    itemId: String(f.targetId),
    parent: null,
    // Wherever it has been put, which is the origin until somebody moves it. Everything
    // seated on it is written in ITS frame, so this moves the whole fitting at once.
    seat: { pos: f.targetSeat, rot: quatFromEulerDeg(f.targetSpin) },
    axis: [0, 0, 1],
    approach: 0,
    step: 0,
    order: 1,
    ...innerOf(f, 'target'),
  };
  const moving: Instance = {
    id: 'moving',
    itemId: String(f.movingId),
    parent: 'target',
    // INTO THE FIXED PART'S FRAME, which is where this instance's seat is written and which
    // the fixed part may itself be turned in. Both spins are stored in the assembly's frame,
    // so what belongs here is "the moving part's orientation, as seen from the target" —
    // target⁻¹ ∘ moving. Identical target and moving spins therefore compose to no rotation
    // at all, which is right: a part turned exactly as the thing it seats on is turned is
    // square to it.
    seat: { pos: f.seat, rot: qCompose(qInvert(quatFromEulerDeg(f.targetSpin)), quatFromEulerDeg(f.spin)) },
    axis: f.axis,
    // The helix is part of the journey, not extra to it: a 40 mm travel with 12 mm of thread
    // is 28 mm of approach and then 12 mm turning, so the part still starts 40 mm out however
    // the two are split.
    approach: Math.max(0, f.travel - (f.pitch > 0 ? f.depth : 0)),
    ...(f.pitch > 0 && f.depth > 0 ? { thread: { depth: f.depth, pitch: f.pitch } } : {}),
    // TURNED AS IT ARRIVES, about an axis in the fixed part's frame (see Fitting.turn): a barrel nut
    // turned on its way in until its bore faces the screw.
    ...(f.turn ? { turn: f.turn } : {}),
    step: 1,
    // AFTER ITS PREPARATION, where it has one: the washer goes on in the hand, then the screw in.
    order: base,
    ...innerOf(f, 'moving'),
  };
  // AND THE TOOL, IF THERE IS ONE, standing where it has been put. Step 0, like the part
  // being fitted onto: it is already in the picture when the step opens and does not travel
  // — a screwdriver is not driven into anything, it is held. Named by its index in the parts
  // list, which is what the fitting stores; a tool the Parts DB has never seen simply has no
  // mesh to match it, and the viewer draws what it has.
  //
  // OR RIDING ON THE MOVING PART, where a set says where it bears on it and no hand has put it
  // anywhere (see Fitting.toolRide): the moving part's child, so it travels and turns with it.
  const ride = f.toolRide && !toolPlaced(f) ? f.toolRide : null;
  // OR ON THE HARDWARE PREPARED ON IT, each unit in turn (see Fitting.toolOnPrep).
  const onPrep = !!ride && toolOnPrep(f);
  const onNext = !!ride && !onPrep && toolOnNext(f);
  const tool: Instance[] = f.toolId == null ? [] : [{
    id: 'tool',
    itemId: `tool:${f.toolId}`,
    parent: ride ? (onPrep ? prepId(0) : onNext ? nextId(0) : 'moving') : null,
    seat: ride ? { pos: ride, rot: [0, 0, 0, 1] } : { pos: f.toolSeat, rot: quatFromEulerDeg(f.toolSpin) },
    axis: [0, 0, 1],
    approach: 0,
    step: 0,
    order: 1,
  }];
  // AND EVERY OTHER COPY, beside the first: the same part, its own seat. All at once where nothing
  // carries them; ONE AFTER ANOTHER where the tool rides the part (see toolHops), since one hand
  // with one screwdriver turns one barrel nut at a time — each copy its own slice of the step,
  // waiting at the start of it while the tool comes over from the one before.
  const hops = toolHops(f) && tool.length > 0;
  // AND A TOOL RIDING THE PART SEATS ITSELF FIRST, one copy or several: it stands a little off the
  // part, and the part waits while it is pushed home (see Instance.mate).
  const rides = !!ride && tool.length > 0;
  const copies: Instance[] = (f.copies ?? []).map((c, n) => ({
    ...moving,
    id: copyId(n + 1),
    seat: { pos: c.seat, rot: qCompose(qInvert(quatFromEulerDeg(f.targetSpin)), quatFromEulerDeg(c.spin)) },
    axis: c.axis,
    approach: Math.max(0, c.travel - (f.pitch > 0 ? f.depth : 0)),
    ...(hops ? { order: base + n + 1, waits: HOP_SHARE } : {}),
  }));
  // AND THE ONE TOOL, ON EACH COPY IN TURN (see Instance.hops). It was one tool per copy, all
  // turning at once: two screwdrivers in two barrel nuts, which no manual asks of one pair of hands.
  // AND THE HARDWARE PREPARED ON EACH OF THEM (see Fitting.prepOn): on before it goes in, in the
  // first slice, riding it from then on. Square to it, as the set built the two; or as its hole
  // stands it, turned into line on the way in. ONE AFTER ANOTHER where the tool turns each.
  const units = prep ? prepUnits(prep) : [];
  const prepared: Instance[] = [moving, ...copies].flatMap((on, n) => units.map((u, j) => {
    const k = n * units.length + j;
    return {
      id: prepId(k),
      itemId: String(prep!.itemId),
      parent: on.id,
      seat: { pos: u.seat, rot: quatFromEulerDeg(u.spin) },
      axis: u.axis,
      ...threadOf(prep!, u),
      ...(u.turn ? { turn: u.turn } : {}),
      step: 1,
      order: onPrep ? k + 1 : 1,
      ...(onPrep ? { waits: HOP_SHARE } : {}),
    };
  }));
  // The tool's round: the moving part and its copies, or the hardware prepared on them, after
  // which it backs out and is put down while the part goes on.
  // AND THE SECOND HARDWARE, INTO THE PART FITTED ONTO, in the last slice: after the tool is put
  // down, if it has one (see Fitting.nextOn). ONE AFTER ANOTHER where the tool turns each.
  const next = nextOf(f);
  const before = base + (hops ? copies.length : 0);
  // AND ITS OWN PREPARATION FIRST, where it has one (see Fitting.nextPrep): every unit drawn as its
  // slice opens, its washer sliding on, then all of them in.
  const nextPrep = nextPrepOf(f);
  const w = nextPrep ? 1 : 0;
  // ONE UNIT AFTER ANOTHER where a tool turns each: the first one, or the second tool (see
  // Fitting.nextToolId).
  const ride2 = nextToolRides(f) ? f.nextToolRide! : null;
  const each = onNext || !!ride2;
  // AND NOTHING OF IT BEFORE THE TOOL IS PUT DOWN, where the tool leaves the moving part for it (see
  // toolLead): the wood buttons appeared and the camera flew to them while the screwdriver was still
  // backing out of the screw. The first of it waits that beat, then the camera's flight.
  const lead = toolLead(f);
  // The third hardware's slice, and the tool leaving the second's before it (see lastLead); a tool
  // still in the second hardware at the end is put down after it.
  const last = lastOf(f);
  const lastAt = before + w + (each ? nextIds(f).length : 1);
  const lead2 = lastLead(f);
  const done = last ? { after: lastAt, within: HOP_SHARE } : { within: TOOL_OUTRO };
  const opens = (k: number, base?: number) => (lead && k === 0
    ? { waits: lead + HOP_SHARE, enters: before + lead } : base != null ? { waits: base } : {});
  const after: Instance[] = next ? prepUnits(next).map((u, k) => ({
    id: nextId(k),
    itemId: String(next.itemId),
    parent: 'target',
    seat: { pos: u.seat, rot: quatFromEulerDeg(u.spin) },
    axis: u.axis,
    ...threadOf(next, u),
    ...(u.turn ? { turn: u.turn } : {}),
    step: 1,
    order: before + 1 + w + (each ? k : 0),
    ...(rides || ride2 ? { waits: HOP_SHARE } : {}),
    // NOT DRAWN UNTIL ITS TURN: waiting from the first frame, the wood buttons stood on the line of
    // the screws they cover, and the screws went through them on their way in.
    enters: before + (each && !nextPrep ? k : 0) + (lead && (nextPrep || !each || k === 0) ? lead : 0),
    ...(nextPrep ? {} : opens(each ? k : 0, rides || ride2 ? HOP_SHARE : undefined)),
  })) : [];
  const nextPrepared: Instance[] = nextPrep ? after.map((on, k) => ({
    id: nextPrepId(k),
    itemId: String(nextPrep.itemId),
    parent: on.id,
    seat: { pos: nextPrep.seat, rot: quatFromEulerDeg(nextPrep.spin ?? [0, 0, 0]) },
    axis: nextPrep.axis,
    approach: nextPrep.travel,
    step: 1,
    order: before + 1,
    enters: before,
    ...opens(0),
  })) : [];
  // The tool's round: the moving part and its copies, the hardware prepared on them, or the second
  // hardware. After the first two it backs out and is put down while the gesture goes on; to the
  // last it comes in as its turn opens.
  const held: Instance[] = rides ? [{
    ...tool[0],
    hops: onPrep ? prepared.map((p) => p.id) : onNext ? after.map((p) => p.id)
      : ['moving', ...(hops ? copies.map((c) => c.id) : [])],
    mate: MATE_MM,
    ...(onPrep ? { leaves: { after: prepared.length, within: HOP_SHARE } }
      : onNext ? { enters: before + w, leaves: done }
        : next ? { leaves: { after: before, within: HOP_SHARE } } : { leaves: { within: TOOL_OUTRO } }),
  }] : tool;
  if (rides && !onNext) moving.waits = HOP_SHARE;
  // AND THE SECOND TOOL, on each unit of the second hardware in turn, coming in as the first goes in.
  const second: Instance[] = ride2 ? [{
    id: 'tool2',
    itemId: `tool:${f.nextToolId}`,
    parent: nextId(0),
    seat: { pos: ride2, rot: [0, 0, 0, 1] },
    axis: [0, 0, 1],
    approach: 0,
    step: 0,
    order: 1,
    hops: after.map((p) => p.id),
    mate: MATE_MM,
    enters: before + w + (w ? 0 : lead),
    leaves: done,
  }] : [];
  // AND THE THIRD HARDWARE, in a slice of its own after the second's, once the tool turning that one
  // is put down (see Fitting.lastOn).
  const lastUnits: Instance[] = last ? prepUnits(last).map((u, k) => ({
    id: lastId(k),
    itemId: String(last.itemId),
    parent: 'target',
    seat: { pos: u.seat, rot: quatFromEulerDeg(u.spin) },
    axis: u.axis,
    ...threadOf(last, u),
    ...(u.turn ? { turn: u.turn } : {}),
    step: 1,
    order: lastAt + 1,
    enters: lastAt + lead2,
    ...(lead2 ? { waits: lead2 } : {}),
  })) : [];
  const asm = {
    instances: [target, moving, ...copies, ...prepared, ...after, ...nextPrepared, ...lastUnits, ...held, ...second],
    ...(f.opensWide ? { intro: INTRO } : {}),
  };
  return f.mirrorAll ? mirrorAssembly(asm) : asm;
}

// A PART'S PREPARATIONS AS MESHES OF ITS OWN COMING HOME, all in the first slice (see Prep).
const innerOf = (f: Movement, on: Prep['on']): Pick<Instance, 'inner'> => {
  const mine = (f.preps ?? []).filter((p) => p.on === on);
  return mine.length
    ? { inner: mine.map((p) => ({ index: p.index, axis: p.axis, travel: p.travel, order: 1 })) }
    : {};
};

// WHETHER THE TOOL CARRIES THE GESTURE FROM COPY TO COPY: it rides the moving part (see
// Fitting.toolRide; nobody has put it anywhere) and the gesture puts on more than one.
export const toolHops = (f: Pick<Movement, 'toolId' | 'toolRide' | 'toolSeat' | 'toolSpin' | 'copies' | 'toolOnPrep' | 'toolOnNext'>) =>
  f.toolId != null && !!f.toolRide && !toolPlaced(f) && !f.toolOnPrep && !f.toolOnNext && !!f.copies?.length;

// WHETHER THE TOOL TURNS THE SECOND HARDWARE (see Fitting.toolOnNext).
const toolOnNext = (f: Pick<Movement, 'toolId' | 'toolRide' | 'toolSeat' | 'toolSpin' | 'toolOnNext'>) =>
  f.toolId != null && !!f.toolRide && !toolPlaced(f) && !!f.toolOnNext;

// THE MIDDLE OF THE MOVEMENT'S OWN SLICES, 0 to 1 of the clip: after its preparation, before the
// second hardware and the tool's closing beat (see camAt).
export const movementMid = (f: PrepFields) => {
  const all = gestureTurns(f);
  return (prepTurns(f) + all - nextTurns(f) - lastTurns(f) - toolOutro(f)) / 2 / all;
};

// THE BEAT THE TOOL TAKES TO BACK OUT AND BE PUT DOWN between the movement and the second hardware,
// in slices: where it rides the moving part and the second hardware follows. A time slot of its
// own: nothing of the second hardware is drawn, and the camera does not move, until it is gone.
export const toolLead = (f: PrepFields) =>
  (f.toolId != null && !!f.toolRide && !toolPlaced(f) && !toolOnPrep(f) && !toolOnNext(f) && !!nextOf(f) ? HOP_SHARE : 0);

// THE THIRD HARDWARE'S ONE SLICE (see Fitting.lastOn), and the beat a tool turning the second
// hardware takes to be put down before it.
export const lastTurns = (f: PrepFields) => (lastOf(f) ? 1 : 0);
export const lastLead = (f: PrepFields) => (lastOf(f) && (toolOnNext(f) || nextToolRides(f)) ? HOP_SHARE : 0);

// WHETHER A SECOND TOOL TURNS THE SECOND HARDWARE (see Fitting.nextToolId).
const nextToolRides = (f: Pick<Movement, 'nextOn' | 'targetId' | 'nextToolId' | 'nextToolRide'>) =>
  !!nextOf(f) && f.nextToolId != null && !!f.nextToolRide;

// HOW MANY SLICES THE SECOND HARDWARE TAKES: none, one for all of it, or one per unit where the
// tool turns each in turn.
// And one more for its own preparation, before it (see Fitting.nextPrep).
export const nextTurns = (f: PrepFields) =>
  (nextOf(f) ? (toolOnNext(f) || nextToolRides(f) ? nextIds(f).length : 1) + (nextPrepOf(f) ? 1 : 0) : 0);

// WHETHER THE TOOL TURNS THE HARDWARE PREPARED ON THE MOVING PART (see Fitting.toolOnPrep).
const toolOnPrep = (f: Pick<Movement, 'toolId' | 'toolRide' | 'toolSeat' | 'toolSpin' | 'toolOnPrep'>) =>
  f.toolId != null && !!f.toolRide && !toolPlaced(f) && !!f.toolOnPrep;

// HOW MANY SLICES THE PREPARATION TAKES: none, one for all of it, or one per unit where the tool
// turns each in turn.
export const prepTurns = (f: PrepFields) => {
  const prep = prepOf(f);
  if (!f.preps?.length && !prep) return 0;
  return prep && toolOnPrep(f) ? prepIds(f).length : 1;
};

// HOW MANY TIMES THE GESTURE IS MADE IN TURN: once per copy where the tool carries it, once
// otherwise, and once more for a preparation played first. What a sub-step's running time is
// multiplied by (see clipMs).
type PrepFields = Pick<Movement, 'toolId' | 'toolRide' | 'toolSeat' | 'toolSpin' | 'toolOnPrep' | 'toolOnNext' | 'copies' | 'preps' | 'prepOn' | 'nextOn' | 'nextPrep' | 'nextToolId' | 'nextToolRide' | 'lastOn' | 'opensWide' | 'movingId' | 'targetId'>;
export const gestureTurns = (f: PrepFields) =>
  (toolHops(f) ? 1 + f.copies!.length : 1) + prepTurns(f) + nextTurns(f) + lastTurns(f) + toolOutro(f) + introTurns(f);

// THE OPENING OF THE VERY FIRST SUB-STEP, in slices (see Fitting.opensWide): the whole held for
// INTRO_HOLD of it, then the camera flown into the shot.
const INTRO = 0.8;
const INTRO_HOLD = 0.35;
export const introTurns = (f: PrepFields) => (f.opensWide ? INTRO : 0);

// AND THE BEAT AFTER THE LAST, IN SLICES, where a riding tool is still in the last thing it turned
// when the gesture is over: it backs out and is put down (see Instance.leaves). None where it was
// put down earlier, before the part went on, or where it does not ride.
const TOOL_OUTRO = 0.25;
export const toolOutro = (f: PrepFields) =>
  !lastOf(f) && (nextToolRides(f) || (f.toolId != null && !!f.toolRide && !toolPlaced(f) && !toolOnPrep(f) && (toolOnNext(f) || !nextOf(f))))
    ? TOOL_OUTRO : 0;

// The share of a copy's slice the tool spends getting to it — crossing from the one before, and
// seating itself in it.
const HOP_SHARE = 0.25;
// How far off the part a riding tool stands before it seats, in mm.
const MATE_MM = 25;

// WHETHER A HAND HAS PUT THE TOOL SOMEWHERE: anywhere but the origin it starts at, or turned.
export const toolPlaced = (f: Pick<Fitting, 'toolSeat' | 'toolSpin'>) =>
  f.toolSeat.some((v) => v !== 0) || f.toolSpin.some((v) => v !== 0);

// ONE PREPARATION, as the gesture that uses it plays it: which of its two parts is the prepared
// piece, which of that piece's meshes came on last, and the way it came — in the frame the
// piece's meshes are listed in (see Instance.inner).
export type Prep = { on: 'target' | 'moving'; index: number; axis: Vec3; travel: number };

// ONE HARDWARE PREPARED IN THE HAND, as the gesture that puts it in stores it — see Fitting.prepOn.
// Square to it where a set built the two; turned (`spin`, and `turn` on the way in) where a hole
// stands it, and `more` units beside the first, each in its own hole.
export type PrepOn = {
  itemId: number; onto: number; seat: Vec3; axis: Vec3; travel: number;
  spin?: Vec3; turn?: Turn | null; more?: PrepUnit[];
  // AND TURNED IN AT ITS THREAD, where it is a screw its hole rule read one on (see holeMovement):
  // the last `depth` mm of each unit's travel a helix at `pitch` mm a turn, the tool riding it
  // turning with it. 2-2's JCBC screws slid in, and the hex key in them with them.
  pitch?: number; depth?: number;
  // AND ITS OWN SHOT, where it goes into the moving part and is too small or too hidden to be seen
  // in the movement's (see camAt).
  shot?: CamFields;
  // WHICH WORKING OF THE SHOT IT WAS (see PREP_SHOT_V): one worked out by an older one is worked
  // out again, under no sign-off.
  shotV?: number;
  // AND ON WHICH MOVEMENT (see shotFor): the shot stands on the part where the movement starts it,
  // and one worked out before the movement was placed again is worked out again.
  shotFor?: string;
  // WHICH WORKING OF THE HOLE RULE PLACED IT (see PREP_BY_HOLE): one placed by an older one is
  // placed again.
  v?: number;
};
// 2: the zoom counts how far apart the units stand along the view too (see movementShot's zoomOf).
// 3: what hides them is judged from where the camera stands (see hiddenFrom).
// 4: the least hidden view is taken, not the first near it (see shotCandidates).
// 5: the second tool is framed with the second hardware it drives (see Fitting.nextToolId).
// 6: framed for the 9:16 picture it is drawn in, not a 1.8:1 one (see shotShape).
// 7: zoom 1 measured on the scene's own outline, not estimated from boxes (see ShotSizes.fit).
export const PREP_SHOT_V = 7;
// THE MOVEMENT'S OWN SHOT, by the same rules (see Fitting.shotV). 1: the least hidden view, judged
// from where the camera stands. 2: framed for the 9:16 picture (see shotShape). 3: zoom 1 measured
// on the scene (see ShotSizes.fit).
export const MOVE_SHOT_V = 3;
// THE MOVEMENT A HARDWARE'S SHOT WAS WORKED OUT ON: the two parts and where the moving one starts.
// The dowels' shot of 2-1 was taken with the front legs frame where an earlier placing left it, and
// kept after Phase 4 was run again and the frame placed elsewhere.
export const shotFor = (f: Pick<Fitting, 'targetId' | 'movingId' | 'seat' | 'axis' | 'travel' | 'targetSeat' | 'spin' | 'targetSpin' | 'copies' | 'mirrored'>) =>
  JSON.stringify([f.targetId, f.movingId, f.seat, f.axis, f.travel, f.targetSeat, f.spin, f.targetSpin, f.copies ?? null, !!f.mirrored]);
export const shotDone = (on: PrepOn | null | undefined, f: Parameters<typeof shotFor>[0]) =>
  !!on?.shot && on.shotV === PREP_SHOT_V && on.shotFor === shotFor(f);
// 2: the right rail's nuts measured from the right rail (they were from the left one).
// 3: a screw's thread kept (see PrepOn.pitch).
export const PREP_BY_HOLE = 3;
type PrepUnit = { seat: Vec3; spin: Vec3; axis: Vec3; travel: number; turn?: Turn | null };
// A UNIT'S WAY IN: straight, then its thread's helix for the last `depth` (see PrepOn.pitch).
const threadOf = (p: PrepOn, u: PrepUnit): Pick<Instance, 'approach' | 'thread'> => {
  const depth = p.pitch && p.pitch > 0 && p.depth && p.depth > 0 ? Math.min(u.travel, p.depth) : 0;
  return { approach: Math.max(0, u.travel - depth), ...(depth ? { thread: { depth, pitch: p.pitch! } } : {}) };
};
const prepUnits = (p: PrepOn): PrepUnit[] =>
  [{ seat: p.seat, spin: p.spin ?? [0, 0, 0], axis: p.axis, travel: p.travel, turn: p.turn }, ...(p.more ?? [])];

// THE SCENE'S NAME FOR IT, on each copy: 'prep' on the moving part, 'prep#n' on the n-th copy.
export const prepId = (n: number) => (n ? `prep#${n + 1}` : 'prep');
// EVERY ONE OF THEM: each unit on the moving part, then each on every copy.
export const prepIds = (f: Pick<Movement, 'prepOn' | 'movingId' | 'copies'>): string[] => {
  const prep = prepOf(f);
  const n = prep ? prepUnits(prep).length * (1 + (f.copies?.length ?? 0)) : 0;
  return Array.from({ length: n }, (_, k) => prepId(k));
};
// THE SECOND HARDWARE'S (see Fitting.nextOn): 'next', 'next#2'…, and whether it is about the part
// fitted onto now.
export const nextId = (n: number) => (n ? `next#${n + 1}` : 'next');
export const isNext = (instanceId: string) => instanceId === 'next' || instanceId.startsWith('next#');
export const nextOf = (f: Pick<Movement, 'nextOn' | 'targetId'>): PrepOn | null =>
  (f.nextOn && f.nextOn.onto === f.targetId ? f.nextOn : null);
// AND THE THIRD'S (see Fitting.lastOn): 'last', 'last#2'…, about the part fitted onto now, after a
// second hardware.
export const lastId = (n: number) => (n ? `last#${n + 1}` : 'last');
export const isLast = (instanceId: string) => instanceId === 'last' || instanceId.startsWith('last#');
export const lastOf = (f: Pick<Movement, 'nextOn' | 'lastOn' | 'targetId'>): PrepOn | null =>
  (nextOf(f) && f.lastOn && f.lastOn.onto === f.targetId ? f.lastOn : null);
export const lastIds = (f: Pick<Movement, 'nextOn' | 'lastOn' | 'targetId'>): string[] => {
  const last = lastOf(f);
  return last ? prepUnits(last).map((_, k) => lastId(k)) : [];
};
export const nextIds = (f: Pick<Movement, 'nextOn' | 'targetId'>): string[] => {
  const next = nextOf(f);
  return next ? prepUnits(next).map((_, k) => nextId(k)) : [];
};
// AND THE HARDWARE PREPARED ON EACH UNIT OF IT (see Fitting.nextPrep): 'nextPrep', 'nextPrep#2'…,
// and whether it is about the second hardware drawn now.
export const nextPrepId = (n: number) => (n ? `nextPrep#${n + 1}` : 'nextPrep');
export const isNextPrep = (instanceId: string) => instanceId === 'nextPrep' || instanceId.startsWith('nextPrep#');
export const nextPrepOf = (f: Pick<Movement, 'nextOn' | 'nextPrep' | 'targetId'>): PrepOn | null => {
  const next = nextOf(f);
  return next && f.nextPrep && f.nextPrep.onto === next.itemId ? f.nextPrep : null;
};
export const nextPrepIds = (f: Pick<Movement, 'nextOn' | 'nextPrep' | 'targetId'>): string[] =>
  (nextPrepOf(f) ? nextIds(f).map((_, k) => nextPrepId(k)) : []);
export const isPrep = (instanceId: string) => instanceId === 'prep' || instanceId.startsWith('prep#');
// Whether the stored preparation is about the moving part drawn now.
export const prepOf = (f: Pick<Movement, 'prepOn' | 'movingId'>): PrepOn | null =>
  (f.prepOn && f.prepOn.onto === f.movingId ? f.prepOn : null);

// ONE COPY OF THE MOVING PART, past the first — see Fitting.copies.
export type Copy = { seat: Vec3; spin: Vec3; axis: Vec3; travel: number };

// THE SCENE'S NAME FOR EACH COPY: the first is 'moving', as it always was; the n-th after it
// 'moving#n+1'. Whatever is said of the moving part is said of every name this answers true for.
export const copyId = (n: number) => (n ? `moving#${n + 1}` : 'moving');
export const isMovingCopy = (instanceId: string) => instanceId === 'moving' || instanceId.startsWith('moving#');
// AND THE TOOL'S: 'tool', the one tool whatever it rides.
export const isTool = (instanceId: string) => instanceId === 'tool' || instanceId === 'tool2';
